// Import components
const { AreaCalculator } = require("./AreaCalculator.js");
const { PackCalculator } = require("./PackCalculator.js");
const { RoomCalculator } = require("./RoomCalculator.js");
const { CalculatorAccordions } = require("./CalculatorAccordions.js");
const { RugCalculator } = require("./rug/RugCalculator.js");
const { ColourMatches } = require("./ColourMatches.js");

// Import constants
const {
  formatCurrency,
} = require("../constants.js");

// Define component
const ProductCalculator = {
  name: "ProductCalculator",
  components: {
    AreaCalculator,
    PackCalculator,
    RoomCalculator,
    CalculatorAccordions,
    RugCalculator,
    ColourMatches,
  },
  setup() {
    const { ref, computed, onMounted, onUnmounted } = Vue;
    const { useStore } = Vuex;

    const store = useStore();

    const addCartError = ref(null);
    const packsNeeded = computed(() => store.getters.packsNeeded);
    const activeTab = ref("area");
    const productType = computed(() => store.state.productType);
    
    // Check if user has saved rooms (from cookies or server)
    const hasSavedRooms = computed(() => {
      // Check server data for logged-in users
      if (window.fkCalculatorData?.rooms?.length > 0) {
        return true;
      }
      // Check cookies for guests
      try {
        const roomsData = typeof Cookies !== 'undefined' ? Cookies.get("roomsData") : null;
        if (roomsData) {
          const rooms = JSON.parse(roomsData);
          return rooms && rooms.length > 0;
        }
      } catch (e) {}
      return false;
    });
    
    // Default to rooms mode if user has saved rooms
    const useRoomsMode = ref(hasSavedRooms.value);
    const showCreateRoomPanel = ref(false);
    const prefillRoomFromStore = ref(false);
    const backingSelected = ref("action");
    const productPrice = computed(() => store.getters.productPrice);
    const selectedWeldrodOption = ref(
      store.state.weldrodOptions ? store.state.weldrodOptions[0].option : ""
    );
    const addToCartLoading = ref(false);

    // Custom variation selector state
    const selectorOpen = ref(false);
    const searchQuery = ref("");

    // Filtered options based on search query
    const filteredWeldrodOptions = computed(() => {
      if (!store.state.weldrodOptions) return [];
      if (!searchQuery.value) return store.state.weldrodOptions;
      const query = searchQuery.value.toLowerCase();
      return store.state.weldrodOptions.filter((opt) =>
        opt.option.toLowerCase().includes(query)
      );
    });

    // Get the currently selected variation object
    const selectedVariation = computed(() => {
      if (!store.state.weldrodOptions || !selectedWeldrodOption.value) return null;
      return store.state.weldrodOptions.find(
        (opt) => opt.option === selectedWeldrodOption.value
      );
    });

    // Check if ALL options have images
    const allOptionsHaveImages = computed(() => {
      if (!store.state.weldrodOptions || store.state.weldrodOptions.length === 0) return false;
      return store.state.weldrodOptions.every((opt) => opt.variation_image_url);
    });

    // Select a variation and close the panel
    const selectVariation = (option) => {
      selectedWeldrodOption.value = option;
      selectorOpen.value = false;
      searchQuery.value = "";
      // Remove body scroll lock on mobile
      document.body.classList.remove('variation-selector-open');
    };

    // Toggle selector panel
    const toggleSelector = () => {
      selectorOpen.value = !selectorOpen.value;
      if (!selectorOpen.value) {
        searchQuery.value = "";
        // Remove body scroll lock on mobile
        document.body.classList.remove('variation-selector-open');
      } else {
        // Add body scroll lock on mobile (only if screen is small)
        if (window.innerWidth < 768) {
          document.body.classList.add('variation-selector-open');
        }
      }
    };

    // Close selector when clicking outside
    const closeSelectorOnClickOutside = (event) => {
      const selector = document.querySelector('.variation-selector-container');
      if (selector && !selector.contains(event.target)) {
        selectorOpen.value = false;
        searchQuery.value = "";
        document.body.classList.remove('variation-selector-open');
      }
    };

    onMounted(() => {
      document.addEventListener('click', closeSelectorOnClickOutside);
    });

    const inputQuantity = computed({
      get: () => store.state.quantity,
      set: (value) => store.commit("setQuantity", value),
    });

    const addToCart = () => {
      if (productType.value == "pack") {
        return addPackToCart();
      } else if (productType.value == "roll") {
        return addRollToCart();
      }
      return addItemToCart();
    };

    const postItemToCart = async (formData) => {
      addToCartLoading.value = true;

      await fetch(vueAppData.ajax_url, {
        method: "POST",
        body: formData,
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          throw new Error("Server responded with status " + response.status);
        })
        .then((data) => {
          if (data.success) {
            triggerAddToCartStapeEvent(data.data);

            if (
              store.state.freeUpsells == null &&
              (store.state.underlayProducts.length > 0 ||
                store.state.adhesiveProducts.length > 0 ||
                store.state.weldrodProducts.length > 0)
            ) {
              openUpsells(data.data.product);
              var event = new CustomEvent("openMiniCartEvent", {
                detail: { tab: "cart", cart: data.data.cartData, open: false },
              });
              document.dispatchEvent(event);
              return;
            }

            var event = new CustomEvent("openMiniCartEvent", {
              detail: { tab: "cart", cart: data.data.cartData },
            });
            document.dispatchEvent(event);
          } else {
            addCartError.value = data.data;
            console.log(addCartError.value);
          }
        })
        .catch((error) => {
          console.error("Error adding to cart:", error);
        })
        .finally(() => {
          addToCartLoading.value = false;
        });
    };

    const triggerAddToCartStapeEvent = (data) => {
      if (
        typeof window.dataLayer !== "undefined" &&
        typeof window.productDetails !== "undefined"
      ) {
        const product = data.product || {};
        const quantity = product.quantity || store.state.quantity || 1;
        const price =
          parseFloat(product.price) || window.productDetails.price || 0;

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: "add_to_cart_stape",
          ecommerce: {
            currency: "GBP",
            value: price * quantity,
            items: [
              {
                item_id: product.id || window.productDetails.id || "",
                item_name: product.name || window.productDetails.name || "",
                affiliation: window.location.hostname,
                currency: "GBP",
                item_brand:
                  product.attributes?.pa_brand ||
                  window.productDetails.brand ||
                  "",
                item_category:
                  product.attributes?.pa_product_type ||
                  window.productDetails.product_type ||
                  "",
                item_variant: product.variant || "",
                price: price,
                quantity: quantity,
              },
            ],
          },
        });
      }
    };

    const addItemToCart = () => {
      if (store.state.quantity == 0 || store.state.quantity == null) {
        return;
      }

      const formData = new FormData();
      formData.append("action", "add_main_and_upsell_to_cart");
      formData.append("main_product_id", store.state.productId);
      formData.append("main_quantity", store.state.quantity);

      if (selectedWeldrodOption.value) {
        formData.append("weldrod_option", selectedWeldrodOption.value);
      }

      if (store.state.productType == "Rug") {
        if (store.state.selectedSize) {
          formData.append("rug_sku", store.state.selectedSize?.rug_sku);
        } else {
          return;
        }
      }

      postItemToCart(formData);
    };

    const addPackToCart = () => {
      if (store.getters.computedTotal <= 0) {
        return;
      }

      const formData = new FormData();
      formData.append("action", "add_main_and_upsell_to_cart");
      formData.append("main_product_id", store.state.productId);
      formData.append("main_quantity", store.getters.packsNeeded);

      postItemToCart(formData);
    };

    const addRollToCart = () => {
      if (store.getters.computedTotal <= 0) {
        return;
      }

      const formData = new FormData();

      formData.append("roll_width", store.state.width);
      formData.append("roll_length", store.state.length);
      formData.append("total_area", store.state.area);

      formData.append("action", "add_roll_to_cart");
      formData.append("unit_type", "m2");
      formData.append("product_id", store.state.productId);

      if (store.state.backingEnabled) {
        formData.append("backing_type", backingSelected.value);
      }

      postItemToCart(formData);
    };

    const openUpsells = (product) => {
      const openUpsellsEvent = new CustomEvent("openUpsellsEvent", {
        detail: { product, totalComputedArea: store.getters.totalComputedArea },
      });
      document.dispatchEvent(openUpsellsEvent);
    };

    // Single source of truth: disabled when there is no total calculated
    const addToCartDisabled = computed(() => {
      if (store.state.packSize || store.state.rollWidths) {
        return store.getters.computedTotal === 0;
      }
      if (store.state.productType === "Rug") {
        return !store.state.selectedSize;
      }
      return (
        inputQuantity.value == 0 ||
        inputQuantity.value == null
      );
    });

    const ADD_TO_CART_NO_TOTAL_MESSAGE = "Enter details above to calculate a total, then add to cart.";
    const WASTAGE_TOOLTIP_TEXT = "It's a good idea to order a little more flooring than your exact measurement. Rooms vary in size and shape, and you'll often need to cut boards to fit";
    const addToCartDisabledFeedback = ref(null);

    const wastageTooltipOpen = ref(null);
    const wastageTooltipAreaRef = ref(null);
    const toggleWastageMessage = (source) => {
      wastageTooltipOpen.value = wastageTooltipOpen.value === source ? null : source;
    };

    const onAddToCartClick = () => {
      if (addToCartDisabled.value) {
        addToCartDisabledFeedback.value = ADD_TO_CART_NO_TOTAL_MESSAGE;
        return;
      }
      addToCartDisabledFeedback.value = null;
      addToCart();
    };

    const isFinanceEligible = computed(() => store.getters.isFinanceEligible);
    const availableFinanceTerms = computed(
      () => store.getters.availableFinanceTerms
    );
    const maxFinanceTerm = computed(() => store.getters.maxFinanceTerm);
    const minMonthlyPayment = computed(() => store.getters.minMonthlyPayment);
    const computedTotal = computed(() => store.getters.computedTotal);
    
    const openFinanceCalculator = () => {
      if (typeof window.fkOpenFinanceModal === "function") {
        window.fkOpenFinanceModal(
          computedTotal.value,
          availableFinanceTerms.value
        );
      }
    };

    onMounted(() => {
      window.dataLayer = window.dataLayer || [];
    });

    // Watch computedTotal and dispatch event for external listeners
    const { watch } = Vue;
    watch(computedTotal, (newTotal) => {
      // Dispatch custom event for finance calculator hook
      const event = new CustomEvent("fkCalculatorTotalChanged", {
        detail: {
          total: newTotal,
          isFinanceEligible: isFinanceEligible.value,
          availableFinanceTerms: availableFinanceTerms.value,
          maxFinanceTerm: maxFinanceTerm.value,
          minMonthlyPayment: minMonthlyPayment.value,
        },
      });
      document.dispatchEvent(event);
    });

    // Watch selectedWeldrodOption and dispatch event for gallery image swap
    watch(selectedWeldrodOption, (newOption) => {
      if (!store.state.weldrodOptions) return;
      
      // Find the selected variation from weldrodOptions
      const selectedVariation = store.state.weldrodOptions.find(
        (opt) => opt.option === newOption
      );
      
      if (selectedVariation) {
        const event = new CustomEvent("fkVariationImageChanged", {
          detail: {
            option: newOption,
            imageUrl: selectedVariation.variation_image_url || null,
            imageSrcset: selectedVariation.variation_image_srcset || null,
          },
        });
        document.dispatchEvent(event);
      }
    });

    // Clear feedback when a total is calculated (button becomes enabled)
    watch(addToCartDisabled, (disabled) => {
      if (!disabled) addToCartDisabledFeedback.value = null;
    });

    return {
      store,
      addCartError,
      packsNeeded,
      activeTab,
      hasSavedRooms,
      useRoomsMode,
      showCreateRoomPanel,
      prefillRoomFromStore,
      productType,
      backingSelected,
      productPrice,
      selectedWeldrodOption,
      addToCartLoading,
      addToCartDisabled,
      ADD_TO_CART_NO_TOTAL_MESSAGE,
      WASTAGE_TOOLTIP_TEXT,
      addToCartDisabledFeedback,
      onAddToCartClick,
      inputQuantity,
      addToCart,
      postItemToCart,
      formatCurrency,
      isFinanceEligible,
      availableFinanceTerms,
      maxFinanceTerm,
      minMonthlyPayment,
      computedTotal,
      openFinanceCalculator,
      // Custom variation selector
      selectorOpen,
      searchQuery,
      filteredWeldrodOptions,
      selectedVariation,
      selectVariation,
      toggleSelector,
      allOptionsHaveImages,
      wastageTooltipOpen,
      wastageTooltipAreaRef,
      toggleWastageMessage,
    };
  },
  template: `
        <div>
            <!-- Calculator for packs/rolls -->
            <div v-if="store.state.packSize || store.state.rollWidths">
                <!-- Tab Navigation -->
                <ul class="flex justify-between items-center p-0 m-0 list-none" v-if="!store.state.rollWidths">
                    <li 
                        class="py-4 w-full font-bold text-center rounded-t-md cursor-pointer"
                        @click="activeTab = 'area'"
                        :class="{
                            'bg-shades-50': activeTab == 'area',
                            'text-shades-800': activeTab == 'area',
                            'text-shades-500': activeTab != 'area'
                        }"
                    >Calculate Flooring</li>
                    
                    <li 
                        v-if="store.state.packSize"
                        class="py-4 w-full font-bold text-center rounded-t-md cursor-pointer"
                        @click="activeTab = 'packs'"
                        :class="{
                            'bg-shades-50': activeTab == 'packs',
                            'text-shades-800': activeTab == 'packs',
                            'text-shades-500': activeTab != 'packs'
                        }"
                    >Order Packs</li>
                </ul>

                <!-- Calculator Content -->
                <div 
                    class="relative p-5 bg-shades-50"
                    :class="{
                        'rounded-tr-md': activeTab == 'area',
                        'rounded-tl-md': activeTab == 'packs',
                        'rounded-t-md': store.state.rollWidths
                    }"
                >
                    <!-- Calculate Flooring Tab Content -->
                    <div v-if="activeTab == 'area' && !store.state.rollWidths">
                        
                        <!-- Show toggle when user has saved rooms OR has clicked to create a room -->
                        <template v-if="hasSavedRooms || showCreateRoomPanel">
                            <div class="flex gap-2 p-1 mb-4 rounded-lg bg-shades-100">
                                <button 
                                    @click="useRoomsMode = true"
                                    class="flex gap-2 justify-center items-center px-3 py-2 w-1/2 text-sm font-medium rounded-md transition-all"
                                    :class="useRoomsMode ? 'bg-white text-shades-800 shadow-sm' : 'text-shades-500 hover:text-shades-700'"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                                    </svg>
                                    {{ hasSavedRooms ? 'Saved Rooms' : 'Create Room' }}
                                </button>
                                <button 
                                    @click="useRoomsMode = false"
                                    class="flex gap-2 justify-center items-center px-3 py-2 w-1/2 text-sm font-medium rounded-md transition-all"
                                    :class="!useRoomsMode ? 'bg-white text-shades-800 shadow-sm' : 'text-shades-500 hover:text-shades-700'"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                                    </svg>
                                    Enter Area
                                </button>
                            </div>
                            
                            <!-- Rooms Calculator -->
                            <div v-if="useRoomsMode" class="pb-5">
                                <RoomCalculator :prefillFromStore="prefillRoomFromStore" @room-saved="showCreateRoomPanel = true" />
                            </div>
                            
                            <!-- Manual Area Calculator (no "save as room" link since user already knows about rooms) -->
                            <div v-else class="pb-5">
                                <AreaCalculator  />
                            </div>
                        </template>
                        
                        <!-- NO SAVED ROOMS and not creating: Show simple area calculator with prompts -->
                        <template v-else>
                            <div class="pb-5">
                                <AreaCalculator  />
                            </div>
                            
                            <!-- Room creation prompts -->
                            <div class="pb-5">
                                <!-- If area has a value, show "Save this as a room" -->
                                <button 
                                    v-if="store.state.area > 0"
                                    @click="showCreateRoomPanel = true; useRoomsMode = true; prefillRoomFromStore = true"
                                    class="flex gap-2 items-center text-sm font-medium transition-colors btn-text-red"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
                                    </svg>
                                    Save this as a room
                                </button>
                                
                                <!-- Otherwise show "Create a room" prompt -->
                                <button 
                                    v-else
                                    @click="showCreateRoomPanel = true; useRoomsMode = true; prefillRoomFromStore = false"
                                    class="flex gap-2 items-center text-sm font-medium transition-colors text-shades-500 hover:text-shades-700"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                                    </svg>
                                    Create a room to save your measurements
                                </button>
                            </div>
                        </template>
                    </div>
                    
                    <!-- Other tabs (Packs) or Rolls -->
                    <div v-else class="pb-5">
                        <AreaCalculator v-if="activeTab == 'area'"  />
                        <PackCalculator v-if="activeTab == 'packs'" />
                    </div>

                    <!-- Wastage section: tooltips open on click/touch -->
                    <div v-if="productType == 'pack' && computedTotal > 0" ref="wastageTooltipAreaRef">
                        <!-- Wastage Option: show when user doesn't have 10% extra yet, or when they've manually enabled it (so they can turn it off) -->
                        <div v-if="store.getters.showWastageOption || store.state.recommendedWastage" class="flex items-center pb-5 space-x-2 text-base font-normal text-shades-700 recommendedWastage">
                            <input id="wastage-check" type="checkbox" v-model="store.state.recommendedWastage" />
                            <label for="wastage-check" class="text-sm text-shades-700">Add 10% for wastage (Recommended)</label>
                            <span
                                class="inline-flex relative cursor-pointer shrink-0 touch-manipulation"
                                @click.stop="toggleWastageMessage('checkbox')"
                                aria-label="More about wastage"
                                role="button"
                                tabindex="0"
                                @keydown.enter.prevent="toggleWastageMessage('checkbox')"
                                @keydown.space.prevent="toggleWastageMessage('checkbox')"
                            >
                                <svg class="w-4 h-4 text-shades-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                </span>
                            </div>
                                

                        <!-- 10% wastage included (when order already has enough extra from rounding, and user hasn't manually enabled wastage) -->
                        <div v-if="store.getters.hasBuiltInWastage && !store.state.recommendedWastage" class="flex items-center pb-5 space-x-2 text-base font-normal text-shades-700">
                            <span class="text-sm text-shades-700">10% wastage is included</span>
                            <span
                                class="inline-flex relative cursor-pointer shrink-0 touch-manipulation"
                                @click.stop="toggleWastageMessage('included')"
                                aria-label="More about wastage"
                                role="button"
                                tabindex="0"
                                @keydown.enter.prevent="toggleWastageMessage('included')"
                                @keydown.space.prevent="toggleWastageMessage('included')"
                            >
                                <svg class="w-4 h-4 text-shades-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                </span>
                          </div>

                        <div v-if="wastageTooltipOpen" class="mb-5 text-sm text-shades-700">
                            <div v-if="wastageTooltipOpen === 'checkbox'">
                                {{ WASTAGE_TOOLTIP_TEXT }}
                            </div>
                            <div v-if="wastageTooltipOpen === 'included'">
                                {{ WASTAGE_TOOLTIP_TEXT }}
                            </div>
                        </div>
                    </div>

                    <!-- Backing Options -->
                    <div v-if="store.state.backingEnabled || store.state.hybridEnabled" class="pb-3">
                        <div class="input-label">Backing</div>
                        <div class="flex flex-wrap items-center space-x-2 sm:flex-nowrap text-shades-500">
                            <div 
                                v-if="store.state.backingEnabled"
                                class="backing-button"
                                @click="backingSelected = 'action'"
                                :class="{ active: backingSelected == 'action' }"
                            >Action</div>
                            <div 
                                v-if="store.state.backingEnabled"
                                class="backing-button"
                                @click="backingSelected = 'felt'"
                                :class="{ active: backingSelected == 'felt' }"
                            >Felt</div>
                            <div 
                                v-if="store.state.hybridEnabled"
                                class="backing-button"
                                @click="backingSelected = 'hybrid'"
                                :class="{ active: backingSelected == 'hybrid' }"
                            >Hybrid</div>
                        </div>
                    </div>

                    <!-- Price Display -->
                    <div v-if="computedTotal > 0">
                        <div class="pb-3 text-3xl font-semibold leading-none text-shades-800" id="total-price">
                            {{ formatCurrency(store.getters.computedTotal) }}
                            <span class="text-sm font-normal">inc VAT</span>
                        </div>
                        <div class="flex items-center text-sm leading-tight text-shades-500">
                            <div v-if="store.state.packSize">
                                The total price above is for
                                <span class="font-bold">{{ packsNeeded }} {{ "pack" + (packsNeeded == 1 ? "" : "s") }}</span>
                                of flooring, covering an area of
                                <span class="font-bold">{{ store.getters.totalComputedArea.toFixed(2) + store.getters.unitSquaredText }}</span>
                            </div>
                            <div v-else-if="store.state.rollWidths">
                                The total price above is for
                                <span class="font-bold">{{ store.getters.totalComputedArea.toFixed(2) + store.getters.unitSquaredText }}</span>
                                of flooring
                            </div>
                        </div>
                    </div>

                    <!-- Add to Cart Button -->
                    <div class="pt-5">
                        <button
                            @click="onAddToCartClick"
                            class="w-full btn btn-red btn-icon add-to-cart-btn-main"
                            :class="{ 'btn-disabled': addToCartDisabled }"
                            :title="addToCartDisabled ? ADD_TO_CART_NO_TOTAL_MESSAGE : ''"
                            :aria-disabled="addToCartDisabled"
                        >
                            <svg v-if="addToCartLoading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="animate-spin">
                                <path class="fa-secondary" opacity=".4" d="M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z" />
                                <path class="fa-primary" d="M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z" />
                            </svg>
                            <span v-if="!addToCartLoading">Add to cart</span>
                            <svg v-if="!addToCartLoading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                                <path d="M248 72V48H200V72 232H40 16v48H40 200V440v24h48V440 280H408h24V232H408 248V72z" />
                            </svg>
                        </button>
                    </div>

                    <div v-if="addToCartDisabledFeedback && addToCartDisabled" class="pt-5 w-full text-sm text-center text-red-500">{{ addToCartDisabledFeedback }}</div>
                    <div v-if="addCartError" class="pt-5 w-full text-sm text-center text-red-500">{{ addCartError }}</div>
                </div>
            </div>
            <div v-else>
                <RugCalculator v-if="store.state.productType == 'Rug'" :postItemToCart="postItemToCart"
                :addToCartLoading="addToCartLoading" ref="rugCalculator" />
                
                <!-- Regular accessory without weldrod options -->
                <div v-if="store.state.productType == 'accessory' && !store.state.weldrodOptions" class="p-5 rounded-t-md bg-shades-50">
                    <div class="mb-[30px]">
                        <div class="w-full mb-[10px] text-shades-700">Quantity</div>
                        <div class="flex justify-between items-center">
                            <input class="input-field" type="number" v-model="inputQuantity" min="1" />
                            <div class="text-[30px] font-semibold text-shades-800">
                            {{ formatCurrency(inputQuantity * productPrice) }}
                            <span class="text-sm font-normal">inc VAT</span>
                            </div>
                        </div>
                    </div>
                <div>
                    <button @click="onAddToCartClick" class="w-full btn btn-secondary btn-icon"
                    :class="{ 'btn-disabled': addToCartDisabled }"
                    :title="addToCartDisabled ? ADD_TO_CART_NO_TOTAL_MESSAGE : ''"
                    :aria-disabled="addToCartDisabled">
                        <svg v-if="addToCartLoading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="animate-spin">
                            <path class="fa-secondary" opacity=".4"
                            d="M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z" />
                            <path class="fa-primary"
                            d="M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z" />
                        </svg>

                        <span v-if="!addToCartLoading">Add to cart</span>
                        <svg v-if="!addToCartLoading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                            <path d="M248 72V48H200V72 232H40 16v48H40 200V440v24h48V440 280H408h24V232H408 248V72z" />
                        </svg>
                    </button>
                </div>
                    <div v-if="addToCartDisabledFeedback && addToCartDisabled" class="pt-5 w-full text-sm text-center text-red-500">{{ addToCartDisabledFeedback }}</div>
                    <div v-if="addCartError" class="pt-5 w-full text-sm text-center text-red-500">{{ addCartError }}</div>
                </div>

                <div class="mb-[30px]" v-if="store.state.weldrodOptions">
                    <ColourMatches :postItemToCart="postItemToCart" :addToCartLoading="addToCartLoading" />

                    <div class="w-full mb-[10px] text-shades-700 text-sm">Select a colour</div>
                    
                    <div class="relative mb-6 variation-selector-container">
                        <!-- Trigger Button -->
                        <button 
                            @click.stop="toggleSelector" 
                            type="button"
                            class="flex gap-2 justify-between items-center p-3 mb-2 w-full bg-white rounded-lg border border-solid transition-all duration-200 cursor-pointer variation-trigger border-shades-100 hover:border-shades-300"
                        >
                            <div class="flex gap-2 items-center min-w-0">
                                <template v-if="allOptionsHaveImages">
                                    <div 
                                        v-if="selectedVariation?.variation_image_url" 
                                        class="overflow-hidden flex-shrink-0 w-10 h-10 rounded bg-shades-100"
                                    >
                                        <img 
                                            :src="selectedVariation.variation_image_url + '?width=80&height=80&quality=85&format=webp'" 
                                            :alt="selectedWeldrodOption"
                                            class="object-cover w-full h-full"
                                        />
                                    </div>
                                </template>
                                <span class="text-sm truncate text-shades-800">{{ selectedWeldrodOption }}</span>
                            </div>
                            <svg 
                                class="flex-shrink-0 w-4 h-4 transition-transform duration-200 text-shades-500" 
                                :class="{ 'rotate-180': selectorOpen }"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        <div 
                            v-if="selectorOpen"
                            class="variation-backdrop"
                            @click="toggleSelector"
                        ></div>
                        
                        <!-- Panel -->
                        <div 
                            v-if="selectorOpen"
                            class="overflow-hidden rounded-lg border shadow-lg variation-panel border-shades-200"
                            @click.stop
                        >
                            <!-- Mobile Header with close button -->
                            <div class="variation-panel-header">
                                <span class="text-base font-semibold text-shades-800">Select a colour</span>
                                <button 
                                    @click="toggleSelector"
                                    class="p-1.5 rounded-full transition-colors hover:bg-shades-100"
                                >
                                    <svg class="w-5 h-5 text-shades-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <!-- Search Box -->
                            <div class="p-2 bg-white border-b border-shades-100">
                                <div class="flex relative items-center">
                                    <svg class="absolute left-2.5 w-4 h-4 pointer-events-none text-shades-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <input 
                                        v-model="searchQuery"
                                        type="text" 
                                        placeholder="Search colours..." 
                                        class="py-2 pr-3 pl-10 w-full text-sm rounded-lg border transition-all bg-shades-50 border-shades-200 focus:ring-1 focus:ring-brand-300 focus:bg-white"
                                        @click.stop
                                    />
                                </div>
                            </div>

                            <!-- Options List -->
                            <div class="variation-options-list">
                                <div 
                                    v-for="(option, index) in filteredWeldrodOptions" 
                                    :key="index"
                                    @click="selectVariation(option.option)"
                                    class="flex gap-2 items-center p-2 transition-colors cursor-pointer hover:bg-shades-50"
                                    :class="{ 'bg-brand-300/10': option.option === selectedWeldrodOption }"
                                >
                                    <!-- Option Thumbnail (only if ALL options have images) -->
                                    <template v-if="allOptionsHaveImages">
                                        <div 
                                            v-if="option.variation_image_url" 
                                            class="overflow-hidden flex-shrink-0 w-10 h-10 rounded border transition-all"
                                            :class="option.option === selectedWeldrodOption ? 'border-shades-800' : 'border-shades-200'"
                                        >
                                            <img 
                                                :src="option.variation_image_url + '?width=80&height=80&quality=85&format=webp'" 
                                                :alt="option.option"
                                                class="object-cover w-full h-full"
                                                loading="lazy"
                                            />
                                        </div>
                                    </template>

                                    <!-- Option Name -->
                                    <span 
                                        class="flex-1 text-sm"
                                        :class="option.option === selectedWeldrodOption ? 'text-shades-600 font-medium' : 'text-shades-700'"
                                    >{{ option.option }}</span>

                                    <!-- Checkmark for selected -->
                                    <svg 
                                        v-if="option.option === selectedWeldrodOption"
                                        class="flex-shrink-0 w-4 h-4 text-shades-600" 
                                        fill="currentColor" viewBox="0 0 20 20"
                                    >
                                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                    </svg>
                                </div>

                                <!-- No results -->
                                <div v-if="filteredWeldrodOptions.length === 0" class="p-6 text-center text-shades-500">
                                    <p class="text-sm">No colours found</p>
                                </div>
                            </div>
                        </div>
                    </div>
                <div class="mb-[30px]">
                    <div class="w-full mb-[10px] text-shades-700">Quantity</div>
                    <div class="flex justify-between items-center">
                        <input class="input-field" type="number" v-model="inputQuantity" />
                        <div class="text-[30px] font-semibold text-shades-800">
                        {{ formatCurrency(inputQuantity * productPrice) }}
                        <span class="text-sm font-normal">inc VAT</span>
                        </div>
                    </div>
                </div>
                <div>
                    <button @click="onAddToCartClick" class="w-full btn btn-secondary btn-icon"
                    :class="{ 'btn-disabled': addToCartDisabled }"
                    :title="addToCartDisabled ? ADD_TO_CART_NO_TOTAL_MESSAGE : ''"
                    :aria-disabled="addToCartDisabled">
                        <svg v-if="addToCartLoading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="animate-spin">
                            <path class="fa-secondary" opacity=".4"
                            d="M0 256C0 397.4 114.6 512 256 512c94.7 0 177.5-51.5 221.7-128l-55.4-32c-33.2 57.4-95.2 96-166.3 96C150 448 64 362 64 256S150 64 256 64l0-64C114.6 0 0 114.6 0 256z" />
                            <path class="fa-primary"
                            d="M477.7 384c21.8-37.7 34.3-81.4 34.3-128C512 114.6 397.4 0 256 0l0 64c106 0 192 86 192 192c0 35-9.4 67.8-25.7 96l55.4 32z" />
                        </svg>

                        <span v-if="!addToCartLoading">Add to cart</span>
                        <svg v-if="!addToCartLoading" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                            <path d="M248 72V48H200V72 232H40 16v48H40 200V440v24h48V440 280H408h24V232H408 248V72z" />
                        </svg>
                    </button>
                </div>
                <div v-if="addToCartDisabledFeedback && addToCartDisabled" class="pt-5 w-full text-sm text-center text-red-500">{{ addToCartDisabledFeedback }}</div>
            </div>
        </div>
    `,
};

// Export the component using module.exports
module.exports = { ProductCalculator };
